/**
 * Runtime AI Calling lists loaded from ATS admin config.
 * Fallback defaults stay in prompt-defaults / prompt-validator — this module
 * only holds overrides. Empty config → callers keep using those fallbacks.
 */

function emptyOverride() {
  return {
    screeningInfields: null,
    driveInfields: null,
    initiateInfields: null,
    driveInitiateInfields: null,
    screeningOutcomes: null,
    driveOutcomes: null,
    languages: null,
    voices: null,
    agentSlots: null,
    requiredPromptSections: null,
    requiredPromptTokens: null,
    runtimePromptVariables: null,
    presetPromptLines: null,
    schemaSlotLimit: null,
    infieldAliases: null,
    requiredInfields: null,
  }
}

let override = emptyOverride()

export const FALLBACK_AGENT_SLOTS = [
  {
    key: 'agentIdFresh',
    inputId: 'ai-agent-fresh',
    label: 'Screening',
    hint: 'Who talks to fresh candidates',
    callType: 'fresh',
  },
  {
    key: 'agentIdDrive',
    inputId: 'ai-agent-drive',
    label: 'Drive',
    hint: 'Walk-in / drive',
    callType: 'drive',
  },
  {
    key: 'agentIdOther',
    inputId: 'ai-agent-other',
    label: 'Other',
    hint: 'Custom script',
    callType: 'other',
  },
]

export const INFIELD_TYPE_OPTIONS = [
  { value: 'TEXT', text: 'Text' },
  { value: 'NUMBER', text: 'Number' },
  { value: 'BOOLEAN', text: 'Yes / No' },
  { value: 'FIXED', text: 'Fixed list' },
]

export const OUTCOME_TYPE_OPTIONS = [
  { value: 'TEXT', text: 'Text' },
  { value: 'BOOLEAN', text: 'Yes / No' },
  { value: 'FIXED', text: 'Fixed list' },
]

function asNonEmptyList(value) {
  return Array.isArray(value) && value.length ? value : null
}

function unwrapConfig(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  let src = raw
  if (src.data && typeof src.data === 'object' && !Array.isArray(src.data)) {
    src = src.data
  }
  if (src.data && typeof src.data === 'object' && !Array.isArray(src.data)
    && (src.data.screeningInfields || src.data.schemas || src.data.screening_infields)) {
    src = src.data
  }
  if (src.schemas && typeof src.schemas === 'object' && !Array.isArray(src.schemas)) {
    src = { ...src, ...src.schemas }
  }
  return src
}

function clone(value) {
  if (value == null) return value
  if (typeof structuredClone === 'function') return structuredClone(value)
  return JSON.parse(JSON.stringify(value))
}

export function normalizeInfieldEntry(item) {
  if (typeof item === 'string') {
    const key = item.trim()
    if (!key) return null
    return {
      key,
      label: key,
      type: 'TEXT',
      required: false,
      visible: true,
    }
  }
  if (!item || typeof item !== 'object') return null
  const key = String(item.key || item.field_name || item.name || item.field || '').trim()
  if (!key) return null
  const type = String(item.type || item.field_type || 'TEXT').toUpperCase() || 'TEXT'
  return {
    key,
    label: String(item.label || item.field_label || key).trim() || key,
    type,
    required: item.required === true,
    visible: item.visible !== false && item.is_visible !== false,
  }
}

export function normalizeOutcomeEntry(item) {
  if (typeof item === 'string') {
    const name = item.trim()
    if (!name) return null
    return {
      outcome_name: name,
      outcome_description: name,
      outcome_type: 'TEXT',
      outcome_enum: null,
      is_visible: true,
    }
  }
  if (!item || typeof item !== 'object') return null
  const name = String(
    item.outcome_name || item.key || item.name || item.outcome || '',
  ).trim()
  if (!name) return null
  let enumList = item.outcome_enum || item.enum || item.options || null
  if (typeof enumList === 'string') {
    enumList = enumList.split(',').map(s => s.trim()).filter(Boolean)
  }
  if (Array.isArray(enumList)) {
    enumList = enumList.map(v => String(v).trim()).filter(Boolean)
    if (!enumList.length) enumList = null
  } else {
    enumList = null
  }
  return {
    outcome_name: name,
    outcome_description: String(
      item.outcome_description || item.label || item.description || name,
    ).trim() || name,
    outcome_type: String(item.outcome_type || item.type || 'TEXT').toUpperCase() || 'TEXT',
    outcome_enum: enumList,
    is_visible: item.is_visible !== false && item.visible !== false,
  }
}

function normalizeLanguageEntry(item) {
  if (typeof item === 'string') {
    const value = item.trim()
    if (!value) return null
    return { value, text: value }
  }
  if (!item || typeof item !== 'object') return null
  const value = String(item.value || item.code || item.id || item.language || '').trim()
  if (!value) return null
  return {
    value,
    text: String(item.text || item.label || item.name || value).trim() || value,
  }
}

function normalizeVoiceEntry(item) {
  if (typeof item === 'string') {
    const id = item.trim()
    if (!id) return null
    return { id, language: '', name: id, gender: '' }
  }
  if (!item || typeof item !== 'object') return null
  const id = String(item.id || item.voice_id || item.voice || '').trim()
  if (!id) return null
  return {
    id,
    language: String(item.language || item.lang || '').trim(),
    name: String(item.name || item.label || id).trim() || id,
    gender: String(item.gender || '').trim(),
    provider: item.provider ? String(item.provider).trim() : undefined,
    accent: item.accent ? String(item.accent).trim() : undefined,
  }
}

function normalizeSlotEntry(item) {
  if (!item || typeof item !== 'object') return null
  const key = String(item.key || item.formKey || item.field || '').trim()
  if (!key) return null
  return {
    key,
    inputId: String(item.inputId || item.input_id || `ai-agent-${key}`).trim(),
    label: String(item.label || key).trim() || key,
    hint: String(item.hint || item.description || '').trim(),
    callType: String(item.callType || item.call_type || '').trim(),
  }
}

function normalizePresetEntry(item) {
  if (!item || typeof item !== 'object') return null
  const id = String(item.id || item.value || '').trim()
  if (!id) return null
  return {
    id,
    label: String(item.label || item.name || id).trim() || id,
    brief: String(item.brief || '').trim(),
    description: String(item.description || item.hint || '').trim(),
  }
}

function normalizeTokenList(value) {
  if (!Array.isArray(value)) return null
  const keys = value.map(v => String(v || '').trim()).filter(Boolean)
  return keys.length ? keys : null
}

function normalizeInfieldList(value) {
  const list = asNonEmptyList(value)
  if (!list) return null
  const seen = new Set()
  const out = []
  list.forEach((item) => {
    const field = normalizeInfieldEntry(item)
    if (!field || seen.has(field.key)) return
    seen.add(field.key)
    out.push(field)
  })
  return out.length ? out : null
}

function normalizeOutcomeList(value) {
  const list = asNonEmptyList(value)
  if (!list) return null
  const seen = new Set()
  const out = []
  list.forEach((item) => {
    const row = normalizeOutcomeEntry(item)
    if (!row || seen.has(row.outcome_name)) return
    seen.add(row.outcome_name)
    out.push(row)
  })
  return out.length ? out : null
}

function keysFromFallback(fallback) {
  if (!Array.isArray(fallback)) return []
  return fallback.map((item) => {
    if (typeof item === 'string') return item.trim()
    if (!item || typeof item !== 'object') return ''
    return String(item.key || item.field_name || item.name || '').trim()
  }).filter(Boolean)
}

function infieldsFromFallback(fallback) {
  if (!Array.isArray(fallback)) return []
  return fallback.map(normalizeInfieldEntry).filter(Boolean)
}

function outcomesFromFallback(fallback) {
  if (!Array.isArray(fallback)) return []
  return fallback.map(normalizeOutcomeEntry).filter(Boolean)
}

/**
 * Merge admin/ATS config into the in-memory catalog.
 * Only non-empty lists replace the current override (partial saves stay safe).
 */
export function applyAiCallingCatalog(config) {
  const src = unwrapConfig(config)
  if (!src || typeof src !== 'object') return getAiCallingCatalogSnapshot()

  const next = { ...override }
  const screeningInfields = normalizeInfieldList(
    src.screeningInfields || src.screening_infields,
  )
  if (screeningInfields) next.screeningInfields = screeningInfields

  const driveInfields = normalizeInfieldList(src.driveInfields || src.drive_infields)
  if (driveInfields) next.driveInfields = driveInfields

  const initiateInfields = normalizeInfieldList(
    src.initiateInfields || src.initiate_infields,
  )
  if (initiateInfields) next.initiateInfields = initiateInfields

  const driveInitiateInfields = normalizeInfieldList(
    src.driveInitiateInfields
    || src.drive_initiate_infields
    || src.driveExtras
    || src.drive_extras,
  )
  if (driveInitiateInfields) next.driveInitiateInfields = driveInitiateInfields

  const screeningOutcomes = normalizeOutcomeList(
    src.screeningOutcomes || src.screening_outcomes,
  )
  if (screeningOutcomes) next.screeningOutcomes = screeningOutcomes

  const driveOutcomes = normalizeOutcomeList(src.driveOutcomes || src.drive_outcomes)
  if (driveOutcomes) next.driveOutcomes = driveOutcomes

  const languages = asNonEmptyList(src.languages || src.languageOptions || src.language_options)
  if (languages) {
    const rows = languages.map(normalizeLanguageEntry).filter(Boolean)
    if (rows.length) next.languages = rows
  }

  const voices = asNonEmptyList(src.voices || src.voiceOptions || src.voice_options)
  if (voices) {
    const rows = voices.map(normalizeVoiceEntry).filter(Boolean)
    if (rows.length) next.voices = rows
  }

  const slots = asNonEmptyList(src.agentSlots || src.agent_slots)
  if (slots) {
    const rows = slots.map(normalizeSlotEntry).filter(Boolean)
    if (rows.length) next.agentSlots = rows
  }

  const sections = normalizeTokenList(
    src.requiredPromptSections || src.required_prompt_sections,
  )
  if (sections) next.requiredPromptSections = sections

  const runtimeVars = normalizeTokenList(
    src.runtimePromptVariables || src.runtime_prompt_variables,
  )
  if (runtimeVars) next.runtimePromptVariables = runtimeVars

  const tokens = src.requiredPromptTokens || src.required_prompt_tokens
  if (tokens && typeof tokens === 'object' && !Array.isArray(tokens)) {
    const fresh = normalizeTokenList(tokens.fresh || tokens.screening)
    const drive = normalizeTokenList(tokens.drive)
    if (fresh || drive) {
      next.requiredPromptTokens = {
        fresh: fresh || (next.requiredPromptTokens && next.requiredPromptTokens.fresh) || null,
        drive: drive || (next.requiredPromptTokens && next.requiredPromptTokens.drive) || null,
      }
    }
  }

  const presets = src.presetPromptLines || src.preset_prompt_lines || src.openingPresets
  if (presets && typeof presets === 'object' && !Array.isArray(presets)) {
    const fresh = asNonEmptyList(presets.fresh || presets.screening)
    const drive = asNonEmptyList(presets.drive)
    const nextPresets = { ...(next.presetPromptLines || {}) }
    if (fresh) {
      const rows = fresh.map(normalizePresetEntry).filter(Boolean)
      if (rows.length) nextPresets.fresh = rows
    }
    if (drive) {
      const rows = drive.map(normalizePresetEntry).filter(Boolean)
      if (rows.length) nextPresets.drive = rows
    }
    if (nextPresets.fresh || nextPresets.drive) next.presetPromptLines = nextPresets
  }

  const limit = Number(src.schemaSlotLimit || src.schema_slot_limit || src.indusSchemaSlotLimit)
  if (Number.isFinite(limit) && limit > 0) next.schemaSlotLimit = limit

  const aliases = src.infieldAliases || src.infield_aliases
  if (aliases && typeof aliases === 'object' && !Array.isArray(aliases)) {
    next.infieldAliases = { ...aliases }
  }
  const required = normalizeTokenList(src.requiredInfields || src.required_infields)
  if (required) next.requiredInfields = required

  override = next
  return getAiCallingCatalogSnapshot()
}

export function resetAiCallingCatalog() {
  override = emptyOverride()
}

export function getAiCallingCatalogSnapshot() {
  return clone(override)
}

export function getSchemaSlotLimit(fallback = 15) {
  return override.schemaSlotLimit || fallback
}

export function getScreeningCreateInfieldKeys(fallback = []) {
  if (override.screeningInfields) return override.screeningInfields.map(f => f.key)
  return keysFromFallback(fallback)
}

export function getDriveCreateInfieldKeys(fallback = []) {
  if (override.driveInfields) return override.driveInfields.map(f => f.key)
  return keysFromFallback(fallback)
}

export function getInitiateInfieldKeys(fallback = []) {
  if (override.initiateInfields) return override.initiateInfields.map(f => f.key)
  return keysFromFallback(fallback)
}

export function getDriveInitiateInfieldKeys(fallback = []) {
  if (override.driveInitiateInfields) return override.driveInitiateInfields.map(f => f.key)
  return keysFromFallback(fallback)
}

export function getCreateInfieldsForCallType(callType, fallbacks = {}) {
  const isDrive = String(callType || '').toLowerCase() === 'drive'
  if (isDrive) {
    if (override.driveInfields) return clone(override.driveInfields)
    return infieldsFromFallback(fallbacks.drive || fallbacks.DRIVE || [])
  }
  if (override.screeningInfields) return clone(override.screeningInfields)
  return infieldsFromFallback(fallbacks.screening || fallbacks.fresh || fallbacks.FRESH || [])
}

export function getScreeningCreateOutcomes(fallback = []) {
  if (override.screeningOutcomes) return clone(override.screeningOutcomes)
  return outcomesFromFallback(fallback)
}

export function getDriveCreateOutcomes(fallback = []) {
  if (override.driveOutcomes) return clone(override.driveOutcomes)
  return outcomesFromFallback(fallback)
}

export function getCreateOutcomesForCallType(callType, fallbacks = {}) {
  const isDrive = String(callType || '').toLowerCase() === 'drive'
  return isDrive
    ? getDriveCreateOutcomes(fallbacks.drive || fallbacks.DRIVE || [])
    : getScreeningCreateOutcomes(fallbacks.screening || fallbacks.fresh || fallbacks.FRESH || [])
}

export function getLanguageOptions(fallback = []) {
  if (override.languages) return clone(override.languages)
  return Array.isArray(fallback) ? fallback.map(normalizeLanguageEntry).filter(Boolean) : []
}

export function getVoiceCatalog(fallback = []) {
  if (override.voices) return clone(override.voices)
  return Array.isArray(fallback) ? fallback.map(normalizeVoiceEntry).filter(Boolean) : []
}

export function getAgentSlots(fallback = FALLBACK_AGENT_SLOTS) {
  if (override.agentSlots) return clone(override.agentSlots)
  return Array.isArray(fallback) ? fallback.map(normalizeSlotEntry).filter(Boolean) : []
}

export function getRequiredPromptSections(fallback = []) {
  if (override.requiredPromptSections) return [...override.requiredPromptSections]
  return Array.isArray(fallback) ? fallback.map(s => String(s || '').trim()).filter(Boolean) : []
}

export function getRuntimePromptVariables(fallback = []) {
  if (override.runtimePromptVariables) return [...override.runtimePromptVariables]
  return Array.isArray(fallback) ? fallback.map(s => String(s || '').trim()).filter(Boolean) : []
}

export function getRequiredPromptTokens(callType, fallback = []) {
  const isDrive = String(callType || '').toLowerCase() === 'drive'
  const configured = override.requiredPromptTokens
  if (configured) {
    const list = isDrive ? configured.drive : configured.fresh
    if (Array.isArray(list) && list.length) return [...list]
  }
  return Array.isArray(fallback) ? fallback.map(s => String(s || '').trim()).filter(Boolean) : []
}

export function getPresetPromptLinesForType(callType, fallback = []) {
  const isDrive = String(callType || '').toLowerCase() === 'drive'
  const configured = override.presetPromptLines
  if (configured) {
    const list = isDrive ? configured.drive : configured.fresh
    if (Array.isArray(list) && list.length) return clone(list)
  }
  return Array.isArray(fallback) ? fallback.map(normalizePresetEntry).filter(Boolean) : []
}

/** Payload fragment for admin GET/PUT — only lists that are currently overridden. */
export function serializeAiCallingCatalog(form = {}) {
  const src = form && typeof form === 'object' ? form : {}
  const screeningInfields = normalizeInfieldList(src.screeningInfields)
  const driveInfields = normalizeInfieldList(src.driveInfields)
  const initiateInfields = normalizeInfieldList(src.initiateInfields)
  const driveInitiateInfields = normalizeInfieldList(src.driveInitiateInfields)
  const screeningOutcomes = normalizeOutcomeList(src.screeningOutcomes)
  const driveOutcomes = normalizeOutcomeList(src.driveOutcomes)
  const languages = asNonEmptyList(src.languages)
    ? src.languages.map(normalizeLanguageEntry).filter(Boolean)
    : null
  const voices = asNonEmptyList(src.voices)
    ? src.voices.map(normalizeVoiceEntry).filter(Boolean)
    : null
  const agentSlots = asNonEmptyList(src.agentSlots)
    ? src.agentSlots.map(normalizeSlotEntry).filter(Boolean)
    : null
  const requiredPromptSections = normalizeTokenList(src.requiredPromptSections)
  const runtimePromptVariables = normalizeTokenList(src.runtimePromptVariables)
  const requiredPromptTokens = src.requiredPromptTokens && typeof src.requiredPromptTokens === 'object'
    ? {
      fresh: normalizeTokenList(src.requiredPromptTokens.fresh),
      drive: normalizeTokenList(src.requiredPromptTokens.drive),
    }
    : null
  const presetPromptLines = src.presetPromptLines && typeof src.presetPromptLines === 'object'
    ? {
      fresh: asNonEmptyList(src.presetPromptLines.fresh)
        ? src.presetPromptLines.fresh.map(normalizePresetEntry).filter(Boolean)
        : null,
      drive: asNonEmptyList(src.presetPromptLines.drive)
        ? src.presetPromptLines.drive.map(normalizePresetEntry).filter(Boolean)
        : null,
    }
    : null

  const payload = {}
  if (screeningInfields) payload.screeningInfields = screeningInfields
  if (driveInfields) payload.driveInfields = driveInfields
  if (initiateInfields) payload.initiateInfields = initiateInfields
  if (driveInitiateInfields) payload.driveInitiateInfields = driveInitiateInfields
  if (screeningOutcomes) payload.screeningOutcomes = screeningOutcomes
  if (driveOutcomes) payload.driveOutcomes = driveOutcomes
  if (languages && languages.length) payload.languages = languages
  if (voices && voices.length) payload.voices = voices
  if (agentSlots && agentSlots.length) payload.agentSlots = agentSlots
  if (requiredPromptSections) payload.requiredPromptSections = requiredPromptSections
  if (runtimePromptVariables) payload.runtimePromptVariables = runtimePromptVariables
  if (requiredPromptTokens && (requiredPromptTokens.fresh || requiredPromptTokens.drive)) {
    payload.requiredPromptTokens = requiredPromptTokens
  }
  if (presetPromptLines && (presetPromptLines.fresh || presetPromptLines.drive)) {
    payload.presetPromptLines = presetPromptLines
  }
  const limit = Number(src.schemaSlotLimit)
  if (Number.isFinite(limit) && limit > 0) payload.schemaSlotLimit = limit
  if (src.infieldAliases && typeof src.infieldAliases === 'object' && !Array.isArray(src.infieldAliases)) {
    payload.infieldAliases = { ...src.infieldAliases }
  }
  const requiredInfields = normalizeTokenList(src.requiredInfields)
  if (requiredInfields) payload.requiredInfields = requiredInfields
  return payload
}

/** Resolved lists for the Admin Fields editor (override or fallback). */
export function resolveCatalogForEditor(fallbacks = {}) {
  return {
    screeningInfields: getCreateInfieldsForCallType('fresh', {
      screening: fallbacks.screeningInfields || [],
    }),
    driveInfields: getCreateInfieldsForCallType('drive', {
      drive: fallbacks.driveInfields || [],
    }),
    initiateInfields: override.initiateInfields
      ? clone(override.initiateInfields)
      : infieldsFromFallback(fallbacks.initiateInfields || []),
    driveInitiateInfields: override.driveInitiateInfields
      ? clone(override.driveInitiateInfields)
      : infieldsFromFallback(fallbacks.driveInitiateInfields || []),
    screeningOutcomes: getScreeningCreateOutcomes(fallbacks.screeningOutcomes || []),
    driveOutcomes: getDriveCreateOutcomes(fallbacks.driveOutcomes || []),
    languages: getLanguageOptions(fallbacks.languages || []),
    voices: getVoiceCatalog(fallbacks.voices || []),
    agentSlots: getAgentSlots(fallbacks.agentSlots || FALLBACK_AGENT_SLOTS),
    requiredPromptSections: getRequiredPromptSections(fallbacks.requiredPromptSections || []),
    requiredPromptTokens: {
      fresh: getRequiredPromptTokens('fresh', fallbacks.requiredPromptTokensFresh || []),
      drive: getRequiredPromptTokens('drive', fallbacks.requiredPromptTokensDrive || []),
    },
    runtimePromptVariables: getRuntimePromptVariables(fallbacks.runtimePromptVariables || []),
    presetPromptLines: {
      fresh: getPresetPromptLinesForType('fresh', fallbacks.presetFresh || []),
      drive: getPresetPromptLinesForType('drive', fallbacks.presetDrive || []),
    },
    schemaSlotLimit: getSchemaSlotLimit(fallbacks.schemaSlotLimit || 15),
  }
}
